import type {ApiErrorResponse} from "@shared/interfaces/apiError";
import type {SlimmedProjectGetDataResponse, SolarPlannerStatusResponse} from "@shared/interfaces/solar";
import {createLogger} from "../logger.ts";

const logger = createLogger("SolarApiClient");

const BACKEND_BASE_URL: string = import.meta.env.VITE_BACKEND_BASE_URL!;

export type OptimizationEffort = "fast" | "thinking";

export const createProjectAndOptimize = async (address: string, countryCode: string, powerConsumption?: number, optimizationEffort?: OptimizationEffort) => {
    const url = new URL(`${BACKEND_BASE_URL}/api/solar/createProjectAndOptimize?address=${address}&countryCode=${countryCode}`);
    if (powerConsumption) {
        url.searchParams.append('powerConsumption', powerConsumption.toString());
    }
    // "fast" is the default and can be omitted; only forward "thinking".
    if (optimizationEffort === 'thinking') {
        url.searchParams.append('optimizationEffort', optimizationEffort);
    }
    return fetch(url)
    .then((response) => response.json())
    .then((data) => {
        logger.debug("Fetched project", { data });

        return data;
    });
};

export const getProjectStatus = async (projectId: number) => {
    const url = new URL(`${BACKEND_BASE_URL}/api/solar/project/${projectId}/getstatus`);
    return fetch(url).then((response) => response.json() as Promise<ApiErrorResponse | SolarPlannerStatusResponse>);
};

export const getProjectData = async (projectId: number) => {
    const url = new URL(`${BACKEND_BASE_URL}/api/solar/project/${projectId}/getdata`);
    return fetch(url).then((response) => response.json() as Promise<ApiErrorResponse | SlimmedProjectGetDataResponse>);
};

export const getGeoJsonUrl = (projectId: number, optimizationType: string = 'SYSTEM_OPTIMISATION_TYPE_PV_ONLY') => {
    return new URL(`${BACKEND_BASE_URL}/api/solar/project/${projectId}/download-geojson/${optimizationType}?includePanelsGrid=true&obstaclesAsFeatures=true`);
};

// @todo Not in use
export const fetchGeoJson = async (projectId: number, optimizationType: string = 'SYSTEM_OPTIMISATION_TYPE_PV_ONLY') => {

    return fetch(getGeoJsonUrl(projectId, optimizationType))
    .then((response) => response.json())
    .then((data) => {
        logger.debug("Fetched project geojson", { data });

        return data;
    });
};

// @todo Not in use
export const confirmRooftop = async (projectId: number, confirmedRooftopData: Record<string, unknown>) => {
    const url = new URL(`${BACKEND_BASE_URL}/api/solar/project/${projectId}/confirm`);
    logger.debug("confirmedRooftopData", { confirmedRooftopData });
    return fetch(url, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify(confirmedRooftopData),
    })
    .then((response) => response.json())
    .then((data) => {
        logger.debug("Fetched project data", { data });

        return data;
    });
};

// @todo Not in use
export const createProjectAndOptimizeWithLatLng = async (latitude: number, longitude: number, countryCode: string, powerConsumption?: number) => {
    const url = new URL(`${BACKEND_BASE_URL}/api/solar/createProjectAndOptimize?latitude=${latitude}&longitude=${longitude}&countryCode=${countryCode}`);
    if (powerConsumption) {
        url.searchParams.append('powerConsumption', powerConsumption.toString());
    }
    return fetch(url)
    .then((response) => response.json())
    .then((data) => {
        logger.debug("Fetched project", { data });

        return data;
    });
};
